import { useTranslation } from 'react-i18next';
import { I18nKey } from '../../../i18n/i18n.key';
import { useTheme } from '../../../themes/theme.provider';
import { useWindow } from '../../../util/window-helper';
import { useTimer } from '../../../hook/timer.hook';
import { secondsToHMS } from '../../../util/timer.util';
import { memo, useEffect, useRef, useState } from 'react';
import { TimerStage } from '../../../enum/timer-stage.enum';
import { useApp } from '../../../hook/app.hook';
import { Question, Section } from '../../../response/test.response';
import { useInvigilate } from '../../../invigilate/invigilate.hook';
import {
  setViolationBlurLock,
  forceClearBlur,
} from '../../../invigilate/invigilate-blur';
import { Modal, Button } from 'react-bootstrap';
import { useSSNavigate } from '../../../navigation/ss-navigate.hook';

const TimerComponent = () => {
  const { isMobile } = useWindow();
  const { t } = useTranslation();
  const { theme, themeType } = useTheme();
  const [timeTaken, setTimeTaken] = useState(0);

  const { testRes, saveResponse, noInternet } = useApp();
  const { stage, startTime, startTimer } = useTimer();
  const { openResult } = useSSNavigate();

  const { fullScreenCloseCount, tabMoveCount } = useInvigilate();

  const intervalId = useRef<any>(null);

  const updateResIntervalId = useRef<any>(null);

  const checkForExpiry = (timeTaken: number) => {
    if (timeTaken > testRes.duration) {
      saveTest(false);
    }
  };

  const startTicker = () => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
    }
    intervalId.current = setInterval(() => {
      if (stage == TimerStage.Started) {
        const currentTime = new Date().getTime();
        startTimeRef.current = startTime;
        const _timeTaken =
          (testRes.timeTaken ? testRes.timeTaken : 0) +
          (currentTime - startTime) / 1000;
        setTimeTaken(_timeTaken);
        checkForExpiry(_timeTaken);
      }
    }, 1000);
  };

  useEffect(() => {
    if (stage == TimerStage.Started) {
      startTicker();
    } else if (intervalId.current) {
      clearInterval(intervalId.current);
    }
  }, [stage]);

  useEffect(() => {
    startTimer();
    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
  }, []);

  const startTimeRef = useRef<any>(
    startTime == 0 ? new Date().getTime() : startTime,
  );
  const testResRef = useRef<any>(testRes);

  const saveTest = (submitted: boolean, isStarted: boolean = false) => {
    const currentTime = new Date().getTime();
    const _timeTaken = (currentTime - startTimeRef.current) / 1000;

    const _testRes = testResRef.current;

    const __testRes = {
      ..._testRes,
      timeTaken: _testRes.timeTaken + _timeTaken,
      sectionList: _testRes.sectionList.map((section: Section) => {
        return {
          ...section,
          ...(section.active && {
            timeTaken: section.timeTaken + _timeTaken,
            questionList: section.questionList.map((question: Question) => {
              return {
                ...question,
                ...(question.active && {
                  timeTaken: question.timeTaken + _timeTaken,
                }),
              };
            }),
          }),
        };
      }),
      isStarted: isStarted,
    };

    saveResponse(__testRes, submitted, openResult);
  };

  const startSaveRes = () => {
    if (updateResIntervalId.current) {
      clearInterval(updateResIntervalId.current);
    }
    updateResIntervalId.current = setInterval(() => {
      saveTest(false);
    }, 20000);
  };

  useEffect(() => {
    saveTest(false, true);
    startSaveRes();
    return () => {
      if (updateResIntervalId.current) {
        clearInterval(updateResIntervalId.current);
      }
    };
  }, []);

  useEffect(() => {
    testResRef.current = testRes;
  }, [testRes]);

  useEffect(() => {
    if (
      testRes.schedule.proctoring.forceFullScreen &&
      (tabMoveCount > 0 || fullScreenCloseCount > 0)
    ) {
      setViolationBlurLock(true);
      setShow(true);
    }
  }, [
    tabMoveCount,
    fullScreenCloseCount,
    testRes.schedule.proctoring.forceFullScreen,
  ]);

  const [show, setShow] = useState(false);
  const [showInternetAlert, setShowInternetAlert] = useState(false);

  const handleCloseInternetAlert = () => {
    setShowInternetAlert(false);
  };

  const handleClose = () => {
    setShow(false);
    setViolationBlurLock(false);
    forceClearBlur();

    if (!testRes.schedule.proctoring.forceFullScreen) {
      return;
    }

    const limitCrossed =
      tabMoveCount >= testRes.schedule.proctoring.screenSwitchLimit ||
      fullScreenCloseCount >= testRes.schedule.proctoring.screenSwitchLimit;

    if (limitCrossed) {
      // Limit exceeded on any device (mobile included) -> submit the test.
      saveTest(true);
    }
    // else if (isMobile) {
    //   // Under the limit on mobile -> reload to re-enter fullscreen.
    //   window.location.reload();
    // }
  };

  // useEffect(() => {
  //   if (noInternet) {
  //     setShowInternetAlert(true);
  //   } else {
  //     setShowInternetAlert(false);
  //   }
  // }, [noInternet]);

  return (
    <>
      <div
        style={{ color: theme.themewhite, minWidth: 204, textAlign: 'left' }}
        className={`${isMobile ? 'ms-auto' : 'mx-3'}`}
      >
        <img
          src={`/images/${
            themeType === 'light' ? 'timer-dark.svg' : 'timer.svg'
          }`}
          alt=""
        />
        <span className="mx-2">
          {t(I18nKey.TEST_TIME)}: {secondsToHMS(testRes.duration - timeTaken)}
        </span>
      </div>

      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton={false}>
          <p className="text-danger">Instruction Violation</p>
        </Modal.Header>
        <Modal.Body>
          Please do not change tab or window Size, Next time we will terminate
          your test!
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              handleClose();
            }}
          >
            Ok
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showInternetAlert}
        onHide={handleCloseInternetAlert}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton={false}>
          <p className="text-danger">No Internet Connection</p>
        </Modal.Header>
        <Modal.Body>
          No internet connection. Please check your internet connection.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              handleCloseInternetAlert();
            }}
          >
            Ok
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default memo(TimerComponent);
